import {
  ActivityFilterKey,
  FeatureName,
  useActivityListFilter,
  useCurrentAccount,
  useCurrentWorkspace,
  useCustomQuery,
  useIsFeatureEnabled,
} from '@gripp/shared-logic';
import {
  CampaignAggregatedActivityData,
  ColumnData,
  ListCountProvider,
  PanelShell,
} from '@gripp/shared-ui';
import { useCallback, useMemo, useState } from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { Activities as ActivitiesOld } from './activities-old';
import { ActivityNew } from './activities-new';
import { ActivityFilters } from './activityFilters';
import { ActivityFoldOneColumn } from './activityFoldOneColumn';

type ActivitiesLocationState = {
  skipAutoSelect?: boolean;
};

type ActivityRow = {
  id: string;
};

/**
 * GraphQL query for the campaign-aggregated activity feed (PR 4 / A1).
 *
 * Nested under `campaign { campaignAggregatedActivities }` — graphback's
 * factory hooks don't auto-unwrap nested namespaces, so we use
 * `useCustomQuery` and read `data.campaign.campaignAggregatedActivities`
 * directly. See AGENTS.md "Graphback-nested queries don't auto-unwrap".
 */
const CAMPAIGN_AGGREGATED_ACTIVITIES = `
  query CampaignAggregatedActivities(
    $filter: CampaignAggregatedActivitiesFilter!
  ) {
    campaign {
      campaignAggregatedActivities(filter: $filter) {
        count
        items {
          campaignId
          title
          oemWorkspaceBrand
          assetCount
          replyCount
          latestActivityAt
          firstMediaThumbnailUrl
          latestMessagePreview
        }
      }
    }
  }
`;

type CampaignAggregatedActivitiesResponse = {
  campaign?: {
    campaignAggregatedActivities?: {
      count?: number;
      items?: CampaignAggregatedActivityData[];
    };
  };
};

export const Activities = () => {
  const isCropCircleEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );
  const location = useLocation();

  if (isCropCircleEnabled) {
    return <ActivitiesBase />;
  }

  if (location.pathname.replace(/\/$/, '') === '/activities/filters') {
    return <Navigate to="/activities" replace />;
  }

  if (location.pathname.replace(/\/$/, '') === '/activities/new') {
    return <ActivityNew />;
  }

  return <ActivitiesOld />;
};


const ActivitiesBase = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { issueId } = useParams();
  const [count, setCount] = useState<number>();
  const skipAutoSelect = (location.state as ActivitiesLocationState | null)
    ?.skipAutoSelect;
  const isActivityRoot = location.pathname.replace(/\/$/, '') === '/activities';

  const workspace = useCurrentWorkspace();
  const { account } = useCurrentAccount();
  const { data: activityFilter } = useActivityListFilter(
    ActivityFilterKey.Activity
  );

  // Originally the resolver 403'd for OEM workspaces, so we mirrored that gate
  // here. While multi-workspace assets are still missing the resolver guard is
  // temporarily relaxed so OEM users can self-test the aggregated card against
  // their own workspace's assets — match that here. Re-add the OEM exclusion
  // when the resolver guard goes back.  Still guard `workspace?.id` — it's
  // undefined on first render (see AGENTS.md).
  //
  // Per ActivityFilterModel convention, the Campaigns toggle is OFF when the
  // marker array contains 'CAMPAIGN'. Hide the aggregated campaign rows when
  // the toggle is OFF — `excludeActivityTypes` in getActivityFilter already
  // hides inline CAMPAIGN issues from the issue list unconditionally.
  const campaignsToggleOff = (activityFilter?.activityTypes ?? []).includes('CAMPAIGN');
  // The aggregated card requires `campaign.read` (resolver enforces it). Viewers
  // without it (e.g. Restricted) get the campaign issues inline instead — see
  // getActivityFilter — so skip the query to avoid a guaranteed FORBIDDEN.
  //
  // Global admins (`*.*`) also get campaigns inline rather than as the card:
  // their feed is cross-workspace, but the card is single-workspace
  // (`viewingWorkspaceId`) and would miss campaigns from every other workspace.
  // Keep this in lockstep with `surfaceCampaignsAsCards` in the shared-ui
  // activityList, which suppresses the inline CAMPAIGN rows for the same set.
  const surfaceCampaignsAsCards =
    (account?.hasPermission('campaign.read') ?? false) &&
    !(account?.isGlobalAdmin() ?? false);
  const aggregatedQueryEnabled =
    !!workspace?.id && !campaignsToggleOff && surfaceCampaignsAsCards;

  const { data: campaignsAggregatedData } =
    useCustomQuery({
      query: CAMPAIGN_AGGREGATED_ACTIVITIES,
      variables: {
        filter: {
          viewingWorkspaceId: workspace?.id ?? '',
          startDate: activityFilter?.startDate || undefined,
          endDate: activityFilter?.endDate || undefined,
        },
      },
      cacheKey: [
        'campaignAggregatedActivities',
        workspace?.id ?? '',
        activityFilter?.startDate ?? '',
        activityFilter?.endDate ?? '',
        campaignsToggleOff ? 'off' : 'on',
      ],
      options: { enabled: aggregatedQueryEnabled },
    }) as { data?: CampaignAggregatedActivitiesResponse };

  const campaignRows: CampaignAggregatedActivityData[] = useMemo(() => {
    const items =
      campaignsAggregatedData?.campaign?.campaignAggregatedActivities?.items ??
      [];
    // Sort newest first by `latestActivityAt`; rows without a timestamp
    // (no replies yet) fall to the end.
    return [...items].sort((a, b) => {
      const aTs = a.latestActivityAt
        ? new Date(a.latestActivityAt).getTime()
        : 0;
      const bTs = b.latestActivityAt
        ? new Date(b.latestActivityAt).getTime()
        : 0;
      return bTs - aTs;
    });
  }, [campaignsAggregatedData]);

  const campaignRowCount = campaignRows.length;

  const onIssueSelected = useCallback(
    (id: string) => {
      navigate(`/activities/${id}`);
    },
    [navigate]
  );

  // First-row auto-select: prefer the most recent campaign aggregated row
  // when present (matches sort order). Falls back to the first Issue row
  // returned by `findIssues`, preserving the legacy behavior.
  const handleDataLoaded = useCallback(
    (data: ActivityRow[]) => {
      if (issueId || skipAutoSelect || !isActivityRoot) return;
      if (campaignRowCount > 0) {
        // Campaign aggregated rows are not yet navigable for Dealers (see
        // activityList.tsx CampaignRowsSection note).  Skip auto-select
        // when they lead the list so the right column stays at "no issue
        // selected" instead of auto-opening an Issue from below the fold.
        return;
      }
      const firstIssueId = data?.[0]?.id;
      if (firstIssueId) {
        onIssueSelected(firstIssueId);
      }
    },
    [
      campaignRowCount,
      isActivityRoot,
      issueId,
      onIssueSelected,
      skipAutoSelect,
    ]
  );

  const handleIssueCountChange = useCallback(
    (issueCount: number) => {
      setCount(issueCount + campaignRowCount);
    },
    [campaignRowCount]
  );

  const handleFilterChange = useCallback(() => {
    navigate('/activities/filters', { state: { skipAutoSelect: true } });
  }, [navigate]);

  const leftColumn: ColumnData = {
    title: 'activity.menu.title',
    count,
    mainComponent: (
      <ActivityFoldOneColumn
        onIssueSelected={onIssueSelected}
        showAssetName={true}
        selectedIssue={issueId}
        onDataLoaded={handleDataLoaded}
        onActivityCountChange={handleIssueCountChange}
        hideFilterBar={true}
        campaignRows={campaignRows}
      />
    ),
    onBackButtonPress: () => {
      navigate('/activities', { state: { skipAutoSelect: true } });
    },
    headerComponent: <ActivityFilters onFilterChange={handleFilterChange} />,
    disableScroll: true,
  };

  return (
    <ListCountProvider count={count}>
      <PanelShell leftColumn={leftColumn} />
    </ListCountProvider>
  );
};
